import React, { useMemo } from "react";
import { ResponsiveHeatMap } from "@nivo/heatmap";
import styles from "./heatMap.module.css";

const TOTAL_KEY = "Total";

// nivo needs numbers (px), so convert rem -> px using the current root font size
const remToPx = (rem) => {
  if (typeof window === "undefined") return rem * 16;
  const root = parseFloat(getComputedStyle(document.documentElement).fontSize);
  return rem * (root || 16);
};

const hexToRgb = (hex) => {
  const h = hex.replace("#", "");
  const full =
    h.length === 3
      ? h
          .split("")
          .map((c) => c + c)
          .join("")
      : h;
  const n = parseInt(full, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};

const sum = (arr) => arr.reduce((a, b) => a + b, 0);

/**
 * Reusable heat map (matrix) component. All size props are in rem.
 * The title, subtitle and badge are expected to come from the parent (e.g. InfoBox).
 *
 * @param {object}  data - API object: { rows, columns, cells, rowTotals?, columnTotals?, grandTotal? }
 * @param {string}  footnote - Text bottom left
 * @param {"position"|"value"} colorMode - Color cells by their position or value (default "position")
 * @param {"column"|"row"|"global"} shading - Value intensity basis when colorMode is "value"
 * @param {string}  lowColor - Lightest shade (default "#e8ecf8")
 * @param {string}  highColor - Darkest shade (default "#2345b8")
 * @param {boolean} showTotals - Show the total row and column (default true)
 * @param {string}  totalLabel - Label for the totals (default "Total")
 * @param {number}  rowHeight - Height per row in rem (default 3.75)
 * @param {number}  labelWidth - Left margin for row labels in rem (default 5.625)
 * @param {number}  height - Fixed chart height in rem; overrides the derived height
 * @param {"cell"|"row"|"column"|"rowColumn"} hoverTarget - What gets highlighted on hover (default "rowColumn")
 * @param {number}  inactiveOpacity - Opacity of the non-highlighted cells, 0 to 1 (default 0.35)
 * @param {boolean} isLoading - Show a shimmer placeholder
 * @param {function} onCellClick - ({ row, column, value })
 */
const HeatMap = ({
  data,
  footnote,
  colorMode = "position",
  shading = "column",
  lowColor = "#e8ecf8",
  highColor = "#2345b8",
  showTotals = true,
  totalLabel = TOTAL_KEY,
  rowHeight = 3.75,
  labelWidth = 5.625,
  height,
  hoverTarget = "rowColumn",
  
  inactiveOpacity = 0.35,
  isLoading,
  onCellClick,
}) => {
  const low = useMemo(() => hexToRgb(lowColor), [lowColor]);
  const high = useMemo(() => hexToRgb(highColor), [highColor]);

  const mix = (t) =>
    `rgb(${low.map((c, i) => Math.round(c + (high[i] - c) * t)).join(",")})`;

  const chartData = useMemo(() => {
    if (!data?.rows?.length || !data?.columns?.length) return [];

    const { rows, columns, cells } = data;

    const rowTotals = data.rowTotals ?? cells.map(sum);
    const columnTotals =
      data.columnTotals ?? columns.map((_, c) => sum(cells.map((r) => r[c])));
    const grandTotal = data.grandTotal ?? sum(rowTotals);

    const colMax = columns.map((_, c) =>
      Math.max(...cells.map((r) => r[c]), 0),
    );
    const rowMax = cells.map((r) => Math.max(...r, 0));
    const globalMax = Math.max(...cells.flat(), 0);

    const maxFor = (r, c) =>
      shading === "row"
        ? rowMax[r]
        : shading === "global"
          ? globalMax
          : colMax[c];

    const body = rows.map((row, r) => ({
      id: row,
      data: [
        ...columns.map((col, c) => {
          const max = maxFor(r, c);
          return {
            x: col,
            y: cells[r][c],
            shade: max ? cells[r][c] / max : 0,
          };
        }),
        ...(showTotals
          ? [{ x: totalLabel, y: rowTotals[r], isTotal: true }]
          : []),
      ],
    }));

    if (!showTotals) return body;

    const totalRow = {
      id: totalLabel,
      data: [
        ...columns.map((col, c) => ({
          x: col,
          y: columnTotals[c],
          isTotal: true,
        })),
        { x: totalLabel, y: grandTotal, isTotal: true },
      ],
    };

    return [...body, totalRow];
  }, [data, shading, showTotals, totalLabel]);

  const getCellShade = (cell) => {
    if (colorMode === "value") return cell.data.shade ?? 0;

    const rowIndex = data?.rows?.indexOf(cell.serieId) ?? -1;
    const columnIndex = data?.columns?.indexOf(cell.data.x) ?? -1;
    const maxPosition =
      (data?.rows?.length ?? 1) + (data?.columns?.length ?? 1) - 2;

    if (rowIndex < 0 || columnIndex < 0 || maxPosition <= 0) return 0.5;
    return 1 - (rowIndex + columnIndex) / maxPosition;
  };

  const rowCount = (data?.rows?.length || 0) + (showTotals ? 1 : 0);
  const chartHeightRem = height ?? rowCount * rowHeight + 3.125; // 3.125rem = top axis space

  return (
    <div className={styles.card}>
      <div style={{ height: `${chartHeightRem}rem` }}>
        {isLoading || chartData.length === 0 ? (
          <div
            className="shimmer"
            style={{ width: "100%", height: "100%", borderRadius: "0.75rem" }}
          />
        ) : (
          <ResponsiveHeatMap
            data={chartData}
            margin={{
              top: remToPx(2.5),
              right: 0,
              bottom: 0,
              left: remToPx(labelWidth),
            }}
            axisTop={{ tickSize: 0, tickPadding: remToPx(0.75) }}
            axisLeft={{ tickSize: 0, tickPadding: remToPx(0.75) }}
            axisRight={null}
            axisBottom={null}
            xInnerPadding={0.04}
            yInnerPadding={0.08}
            borderRadius={remToPx(0.625)}
            borderWidth={0}
            enableLabels
            animate
            hoverTarget={hoverTarget}
            activeOpacity={1}
            inactiveOpacity={inactiveOpacity}
            colors={(cell) => {
              const d = cell.data;
              if (d.isTotal) return "transparent";
              return mix(getCellShade(cell));
            }}
            labelTextColor={(cell) => {
              const d = cell.data;
              if (d.isTotal) return "#334155";
              return getCellShade(cell) > 0.55 ? "#ffffff" : "#1e3a8a";
            }}
            onClick={(cell) => {
              if (cell.data.isTotal) return;
              onCellClick?.({
                row: cell.serieId,
                column: cell.data.x,
                value: cell.data.y,
              });
            }}
            tooltip={({ cell }) =>
              cell.data.isTotal ? null : (
                <div className={styles.tooltip}>
                  {cell.serieId} · {cell.data.x}: <strong>{cell.data.y}</strong>
                </div>
              )
            }
            theme={{
              axis: {
                ticks: {
                  text: {
                    fontSize: remToPx(0.8125),
                    fontWeight: 600,
                    fill: "#64748b",
                  },
                },
              },
              labels: { text: { fontSize: remToPx(0.875), fontWeight: 700 } },
            }}
          />
        )}
      </div>

      <div className={styles.footer}>
        <span>{footnote}</span>
        <div className={styles.legend}>
          <span>Higher risk</span>
          <div
            className={styles.gradient}
            style={{
              background:
                colorMode === "position"
                  ? `linear-gradient(to right, ${highColor}, ${lowColor})`
                  : `linear-gradient(to right, ${lowColor}, ${highColor})`,
            }}
          />
          <span>Lower risk</span>
        </div>
      </div>
    </div>
  );
};

export default HeatMap;
